/**
 * Dialog for configuring per-level options such as pass/fail criteria and timing.
 *
 * @module components/sim/createLevels/LevelOptions
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    FormGroup,
    IconButton,
    LinearProgress,
    Tooltip
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import React, {useEffect, useState} from "react";
import SettingsIcon from "@material-ui/icons/Settings";
import Button from "@material-ui/core/Button";
import Switch from "@material-ui/core/Switch";
import VolumeUpRoundedIcon from "@material-ui/icons/VolumeUpRounded";
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn';
import PropTypes from "prop-types";
import {
    copyHintTextFromLevelOne,
    generateAudio,
    realTimeFeedback,
    sendToSnowfly,
    toggleAutoplayModeSwitch,
    togglePracticeModeSwitch,
    toggleScreenRecordingSwitch,
    updateHintText,
    updateKeywordScore,
} from "../../../services/result.service";
import PassFailCriteria from "./PassFailCriteria";
import ButtonGroup from "@material-ui/core/ButtonGroup";
import useAlert from "../../common/UseAlert";

/**
 * Handles the level options and manages related state updates, side effects, and interactions
 * with other components or data sources within the application.
 *
 * @param {Object} param0 - The parameters for the LevelOptions method.
 * @param {Object} param0.data - The current simulation or level data used for processing.
 * @param {Function} param0.setData - A setter function to update the simulation or level data.
 * @param {Function} param0.updateData - A function to update specific parts of the level data.
 * @param {number} param0.levelIndex - The index of the current level being processed.
 * @param {boolean} param0.singleLevel - Indicates whether the operation is handling a single level.
 * @param {Array} param0.classes - An array of classes associated with the level or application.
 * @param {string} param0.passFailLevel - The ID of the pass/fail level configuration.
 * @param {Function} param0.onOptionChange - A callback invoked when an option is changed.
 * @param {Function} param0.setAlertType - Sets the type of the alert notification (e.g., success, error).
 * @param {Function} param0.setAlertMessage - Sets the message for the alert notification.
 * @param {Function} param0.setOpenAlert - Opens or closes the alert notification component.
 * @param {Function} param0.setLoad - Sets the loading state for asynchronous operations.
 * @param {boolean} param0.load - Indicates whether the system is currently in a loading state.
 *
 * @return {void} This function does not return a value.
 */
const LevelOptions = ({
    data,
    setData,
    updateData,
    levelIndex,
    singleLevel,
    classes,
    passFailLevel,
    onOptionChange,
    setAlertType,
    setAlertMessage,
    setOpenAlert,
    setLoad,
    load
}) => {
    const PLAYMODE_PRACTICE = 1,
        PLAYMODE_AUTOPLAY = 2;

    const {setAlert} = useAlert();
    const [levelTitle, setLevelTitle] = useState('');
    const [open, setOpen] = useState(false);
    const [levelSettingsTooltip, setLevelSettingsTooltip] = useState([]);
    const [keywordScore, setKeywordScore] = useState(false);
    const [hintText, setHintText] = useState(false);
    const [realTimeFeedbackEnabled, setRealTimeFeedbackEnabled] = useState(false);
    const [sendsToSnowfly, setSendsToSnowfly] = useState(false);
    const [screenRecording, setScreenRecording] = useState(false);
    const [passDialog, setPassDialog] = useState(false);
    const [audioOpen, setAudioOpen] = useState(false);
    const [audioCount, setAudioCount] = React.useState(0);
    const [isPassFail, setIsPassFail] = React.useState(false);
    const [copyingHintText, setCopyingHintText] = useState(false);
    const [practiceMode, setPracticeMode] = useState(true);
    const [autoplayMode, setAutoplayMode] = useState(true);
    const [enabled, setEnabled] = useState(false);
    const [realTimeDisabled, setRealTimeDisabled] = useState(false);

    /**
     * A callback function to generate audio files for scripts associated with
     *      a specific simulation level.
     * This function iterates through each script tied to a level, dynamically
     *      fetches or generates the audio resources for each script, and
     *      updates the simulation data structure accordingly.
     *
     * Dependencies:
     * - React's `useCallback` hook for memoizing the function.
     * - Asynchronous functions for fetching and updating data related to audio
     *      resources.
     *
     * Steps:
     * 1. Sets a loading state to indicate the process of generating audio has
     *      started.
     * 2. Iterates over the scripts associated with the simulation level,
     *      identifies the script character, and constructs a request for audio
     *      generation.
     * 3. Updates the corresponding script's audio-related metadata once the
     *      audio resource is successfully generated.
     * 4. Handles conditional updates related to agent-recorded audio based on
     *      script metadata.
     * 5. Updates the simulation data state after processing each script.
     * 6. Concludes the process by resetting the loading state and closing the
     *      audio modal.
     *
     * Parameters:
     * None directly, but relies on the React dependency array for its scope:
     * - `data` (Object): The simulation data containing levels and scripts.
     * - `levelIndex` (number): The index of the current level within the
     *      simulation data.
     * - `setLoad` (Function): A state setter function to toggle the loading
     *      state.
     * - `setData` (Function): A state setter function to update the simulation
     *      data.
     * - `updateData` (Function): A function to update the server/database with
     *      the modified script data.
     */
    const handleLevelAudioGenerate = React.useCallback(
        async () => {
            setLoad && setLoad(true);
            const sim = {...data};
            for (let i = 0; i < sim.levels[levelIndex].scripts.length; i++) {
                const character = data.sim_characters.find(
                    (item) =>
                        item._id === sim.levels[levelIndex].scripts[i].sim_character_id
                );
                const req = {
                    sim_script_id: sim.levels[levelIndex].scripts[i]._id,
                    sim_id: sim.levels[levelIndex].scripts[i].sim_id,
                    voice: character.voice,
                };
                const response = await generateAudio(req);
                sim.levels[levelIndex].scripts[i].audio_url = response.audio_url;
                sim.levels[levelIndex].scripts[i].expiry_date = response.expiry_date;
                sim.levels[levelIndex].scripts[i].audio_key = response.audio_key;
                sim.levels[levelIndex].scripts[i].voice = character.voice;
                if (
                    (
                        (character.type === "agent") &&
                        !sim.levels[levelIndex].scripts[i].agent_recorded_audio
                    ) ||
                    [0, 1].includes(sim.levels[levelIndex].order)
                ) {
                    sim.levels[levelIndex].scripts[i].agent_recorded_audio = true
                }
                setData(sim);
                await updateData(i, sim.levels[levelIndex].scripts[i]);
            }
            setLoad && setLoad(false);
            setAudioOpen(false);

            setEnabled(sim?.levels?.[levelIndex]?.scripts?.[0]?._id);
        },
        [data, levelIndex, setLoad, setData, updateData]
    );

    /**
     * A callback function used to calculate and update the count of scripts with audio URLs
     * for a specific level within the data structure.
     *
     * This function checks the presence of `scripts` at the specified level index within
     * the `data.levels` structure. It filters the scripts that contain non-empty `audio_url` values
     * and updates the `audioCount` state if the count has changed.
     *
     * Dependencies:
     * - `data`: The structured data containing levels and scripts used for filtering.
     * - `levelIndex`: The index of the current level being processed.
     * - `audioCount`: The current count of scripts with audio URLs. It is compared with the recalculated count.
     * - `setAudioCount`: A setter function to update the state of `audioCount` if the count changes.
     *
     * The function ensures that the count of audio-enabled scripts in the application's state is synchronized
     * with the current state of the data.
     */
    const getAudioCount = React.useCallback(
        () => {
            if (data?.levels?.[levelIndex]?.scripts) {
                const result = data?.levels?.[levelIndex]?.scripts?.filter(
                    (script) => script?.audio_url !== ""
                );
                if (result.length <= 0) {
                    //handleLevelAudioGenerate();
                } else if (result.length !== audioCount) {
                    setAudioCount(result.length);
                }
            }

        }, [data, levelIndex, setAudioCount, audioCount]
    );

    /**
     * This effect initializes and synchronizes UI state for the currently
     * selected level whenever relevant inputs change.
     *
     * - Selects the current level from data.levels[levelIndex] (using optional
     *      chaining and nullish coalescing for safety).
     * - If a level exists, updates view state from level fields:
     *     - Title: level.name
     *     - Pass/Fail flag: passFailLevel === level._id
     *     - Toggles: keyword_score, hint_text, real_time_feedback,
     *          send_to_snowfly, screenRecording (each defaulting to false
     *          when absent).
     *     - Creates the tooltips for the toggles that apply for the given level.
     */
    useEffect(() => {
        let level = data?.levels[levelIndex] ?? null;

        if (level) {
            let playMode = level.play_mode;
            if (undefined === playMode) {
                playMode = PLAYMODE_AUTOPLAY | PLAYMODE_PRACTICE;
            }

            setLevelTitle(level.name);
            setIsPassFail((passFailLevel ?? null) === level._id);
            //setImportHint(level.order !== 0 ?? false);
            setKeywordScore(level.keyword_score ?? false);
            setHintText(level.hint_text ?? false);
            setRealTimeFeedbackEnabled(level.real_time_feedback ?? false);
            setSendsToSnowfly(level.send_to_snowfly ?? false);
            setScreenRecording(level.screenRecording ?? false);
            setPracticeMode((playMode & PLAYMODE_PRACTICE) === PLAYMODE_PRACTICE);
            setAutoplayMode((playMode & PLAYMODE_AUTOPLAY) === PLAYMODE_AUTOPLAY);


            let tooltip = [
                {text: 'Import Hints from Level 1 is ', status: level.order !== 0 ?? false},
                {text: 'Keyword Scores are ', status: level.keyword_score ?? false},
                {text: 'Text Hints are ', status: level.hint_text ?? false},
                {text: 'Real time feedback is ', status: level.real_time_feedback ?? false},
                {
                    text: 'Send full audio recording to SnowFly is ',
                    status: level.send_to_snowfly ?? false
                },
                {text: 'Allow screen recording is ', status: level.screenRecording ?? false},
                {text: 'Pass/Fail is ', status: (passFailLevel && (passFailLevel === level._id)) ?? false},
                {text: 'Practice mode is ', status: (playMode & PLAYMODE_PRACTICE) === PLAYMODE_PRACTICE},
                {text: 'Autoplay mode is ', status: (playMode & PLAYMODE_AUTOPLAY) === PLAYMODE_AUTOPLAY},
            ];

            setLevelSettingsTooltip(tooltip);
            getAudioCount();
            setEnabled(level?.scripts?.[0]?._id);

            let count = 0,
                selectedCharacter = data.sim_characters.find(
                    (item) => item.type === "agent"
                );
            if (data.levels[levelIndex].scripts.length > 0) {
                for (let i = 0; i < data.levels[levelIndex].scripts.length; i++) {
                    if (
                        data.levels[levelIndex].scripts[i].sim_character_id ===
                        selectedCharacter._id
                    ) {
                        count++;
                    }
                }
            }

            setRealTimeDisabled(count === 0);
        }
    }, [
        data, levelIndex, singleLevel, setLevelSettingsTooltip, setLevelTitle,
        setKeywordScore, setHintText, setRealTimeFeedbackEnabled, getAudioCount,
        setSendsToSnowfly, setScreenRecording, passFailLevel, setIsPassFail,
        setPracticeMode, setAutoplayMode
    ]);

    /**
     * Emits a custom 'option-changed' event when an option is modified.
     *
     * This function constructs and dispatches a custom event containing details
     * about the changed option, including its name, new value, and whether the
     * operation was successful. The event is dispatched using the 'onOptionChange'
     * handler, if available.
     *
     * @param {string} optionName - The name of the option that was changed.
     * @param {*} value - The new value of the option.
     * @param {boolean} [success=false] - Indicates whether the operation was successful.
     */
    const emitOptionChanged = (optionName, value, success = false) => {
        if (onOptionChange) {
            let evt = new CustomEvent(
                'option-changed',
                {
                    detail: {
                        levelIndex: levelIndex,
                        level: data?.levels[levelIndex] ?? null,
                        option: optionName,
                        value: value,
                        success: success
                    }
                }
            );
            onOptionChange(evt);
        }
    };

    /**
     * Handles the closure of a component or element.
     * This function is typically used as an event handler to update the state of a component,
     * such as closing a dialog or a menu.
     *
     * @function handleClose
     */
    const handleClose = () => {
        setOpen(false);
    };

    /**
     * Manages the state of the password dialog by opening or closing it.
     *
     * @function handlePassDialog
     * @param {string} [text='close'] - Determines the action to perform on the password dialog.
     *                                   Pass 'open' to display the dialog or 'close' to hide it.
     */
    const handlePassDialog = (text = 'close') => {
        if (text === "open") {
            setPassDialog(true);
        }
        if (text === "close") {
            setPassDialog(false);
        }
    };

    /**
     * Handles the click event for the settings button.
     * Toggles the state of the `open` variable to open or close settings.
     *
     */
    const handleSettingsClick = () => {
        setOpen(!open);
    };

    /**
     * Asynchronously handles the import of hint texts from the first level to a specified level.
     *
     * This function retrieves hint text from the scripts of the first level
     * and maps them to corresponding scripts in the specified target level based
     * on the `sim_character_id`. Once imported, the updated data is set, an
     * event is emitted to indicate changes, and a success or error alert is displayed.
     *
     * The mapping ensures that each script in the target level matches
     * its associated script in the first level by `sim_character_id`. If the operation
     * is unsuccessful, an error alert is shown.
     *
     * @async
     * @function handleImportHint
     * @throws {Error} If an issue occurs during the hint text import process.
     */
    const handleImportHint = async () => {
        setCopyingHintText(true);
        let result = await copyHintTextFromLevelOne(
            data.id,
            data.levels[0]._id,
            data.levels[levelIndex]._id
        );
        if (result.data.success) {
            // Copy the hints to the current level's scripts
            const sim = {...data};
            const levelOneScripts = sim.levels[0].scripts;
            let indexDelta = 0;
            sim.levels[levelIndex].scripts.forEach((script, index) => {
                let srcScript = levelOneScripts[index + indexDelta];
                while (script.sim_character_id !== srcScript.sim_character_id) {
                    indexDelta += 1;
                    srcScript = levelOneScripts[index + indexDelta];
                }
                script.hint_text = srcScript.hint_text;
            });
            setData(sim);
            emitOptionChanged('hint_text', true, true);
            setAlert('Hints imported successfully', 'success');
        } else {
            setAlert('Could not import Hints into Level', 'error');
        }
        setCopyingHintText(false);
    };

    /**
     * Asynchronous event handler for updating the keyword score.
     *
     * This function is triggered when there is a change in the keyword score state,
     * typically initiated by a user interaction (e.g., a checkbox toggle). It updates
     * the relevant data object based on the new state of the keyword score and performs
     * an API call to persist these changes. Upon successful update, the local state is also
     * updated to reflect the change.
     *
     * @async
     * @function
     * @param {Object} evt - The event object triggered by the user interaction.
     * @param {Object} evt.target - The target element of the event.
     * @param {boolean} evt.target.checked - Indicates the new state of the keyword
     *      score (true if checked, false otherwise).
     *
     * @throws {Error} If the update API call fails, the change is not persisted
     *      remotely, and an "option changed" signal is emitted indicating failure.
     */
    const handleKeywordChange = async (evt) => {
        const checked = evt.target.checked;
        let changeData = {};
        changeData.sim_id = data.id;
        changeData.keyword_score = checked;
        changeData.level_id = data.levels[levelIndex]._id;
        data.levels[levelIndex].keyword_score =  checked;
        try {
            let result = await updateKeywordScore(changeData);
            if (result.success) {
                setKeywordScore(checked);
                const sim = {...data};
                sim.levels[levelIndex].keyword_score = checked;
                setData(sim);
                emitOptionChanged('keyword_score', checked, true);
            } else {
                emitOptionChanged('keyword_score', checked, false);
            }
        } catch (error) {
            emitOptionChanged('keyword_score', checked, false);
        }
    };

    /**
     * Handles the change of the hint text toggle in the user interface.
     *
     * This asynchronous function is triggered when the hint text toggle state changes.
     * It updates the local state and sends the update to the backend using the `updateHintText` function.
     * Upon a successful backend update, the hint text state is updated both locally and globally
     * (emitting an event to notify other parts of the application). If the update fails,
     * an unsuccessful event is emitted to handle potential errors.
     *
     * @async
     * @function handleHintTextChange
     * @param {Object} evt - The event object associated with the change event of the hint text toggle.
     * It contains information about the toggle's updated checked state.
     * @param {boolean} evt.target.checked - The new state of the hint text toggle checkbox.
     */
    const handleHintTextChange = async (evt) => {
        const checked = evt.target.checked;
        let changeData = {};
        changeData.sim_id = data.id;
        changeData.hint_text = checked;
        changeData.level_id = data.levels[levelIndex]._id;
        data.levels[levelIndex].hint_text = checked;

        let result = await updateHintText(changeData);
        if (result.success) {
            setHintText(checked);
            let sim = {...data};
            sim.levels[levelIndex].hint_text = !!checked;
            setData(sim);
            emitOptionChanged('hint_text', checked, true);
        } else {
            emitOptionChanged('hint_text', checked, false);
        }
    };

    /**
     * Handles the real-time feedback change event and updates the application state accordingly.
     *
     * This asynchronous function manages the enabling or disabling of real-time feedback
     * for specific scripts associated with a simulation. It triggers the appropriate updates
     * on the server and client side based on the event change.
     *
     * @param {Object} evt - The event object representing the user's interaction.
     * It is expected to have a `target` attribute containing the `checked` property,
     * which indicates whether real-time feedback should be enabled (`true`) or disabled (`false`).
     *
     * Function Workflow:
     * - Creates a deep copy of the simulation data.
     * - Identifies the selected agent character within the simulation.
     * - Constructs the request data for the backend based on the current simulation ID,
     *   level ID, and scripts requiring updates.
     * - Updates the local simulation data to reflect the change in real-time feedback settings.
     * - Sends the constructed request data to the backend through a network call.
     * - Updates the loading state during the lifecycle of the asynchronous operation.
     * - Emits events for option changes reflecting the success or failure of the operation.
     *
     * Note: The function assumes the presence of helper functions and variables such as:
     * `setLoad`, `setData`, `realTimeFeedback`, `emitOptionChanged`, and a `levelIndex`
     * that specifies the active level index within the simulation. These must be
     * appropriately defined and available in the surrounding scope for the function to work as intended.
     */
    const handleRealTimeChange = async (evt) => {
        const sim = {...data},
            propName = 'real_time_feedback',
            requestData = {},
            selectedCharacter = sim.sim_characters.find(
                (item) => item.type === "agent"
            ),
            checked = !!evt.target.checked;
        let count = 0;

        requestData.scripts = [];
        requestData.sim_id = data.id;
        requestData.real_time_feedback = checked;
        requestData.level_id = data.levels[levelIndex]._id;
        if (data.id && data.levels[levelIndex].scripts.length > 0) {
            setLoad && setLoad(true);
            for (let i = 0; i < sim.levels[levelIndex].scripts.length; i++) {
                if (
                    sim.levels[levelIndex].scripts[i].sim_character_id ===
                    selectedCharacter._id
                ) {
                    count++;
                    requestData.scripts.push(sim.levels[levelIndex].scripts[i]._id);
                    sim.levels[levelIndex].real_time_feedback = checked;
                    sim.levels[levelIndex].scripts[i].real_time_feedback = checked;
                }
            }

            if (count === 0) {
                setLoad && setLoad(false);
                emitOptionChanged(propName, checked, false);
            } else {
                setData(sim);
                const res = await realTimeFeedback(requestData);
                if (res.success) {
                    setLoad && setLoad(false);
                    emitOptionChanged(propName, checked, true);
                } else {
                    setLoad && setLoad(false);
                    emitOptionChanged(propName, checked, false);
                }
            }
        } else {
            emitOptionChanged(propName, checked, false);
        }
    };

    /**
     * Asynchronously handles the change event for the "Send to Snowfly" option.
     *
     * This function updates the state based on the user's interaction with the checkbox
     * that toggles the "Send to Snowfly" feature. It communicates the change to the server
     * through an API call and updates the local application state accordingly. In case of
     * an error during the API call, it logs the error and emits an event indicating the failure.
     *
     * @async
     * @function
     * @param {Object} evt - Event object associated with the change action.
     * @param {Object} evt.target - The target element of the event.
     * @param {boolean} evt.target.checked - The current checked status of the target element.
     */
    const handleSendToSnowflyChange = async (evt) => {
        const checked = !!evt.target.checked,
            propName = 'send_to_snowfly';

        try {
            const res = await sendToSnowfly({
                simId: data?.levels[levelIndex]?.sim_id,
                levelId: data?.levels[levelIndex]?._id,
                sendToSnowfly: checked,
            });

            setSendsToSnowfly(checked);

            emitOptionChanged(propName, checked, res.success);
        } catch (err) {
            console.error(err);
            emitOptionChanged(propName, checked, false);
        }
    };

    /**
     * Handles the change event for the screen recording toggle switch.
     * Updates the screen recording state and reflects the changes in the application state.
     * Makes an asynchronous API call to persist the change and emits an event
     * indicating the success or failure of the operation.
     *
     * @async
     * @function
     * @param {Object} evt - The event object triggered by the toggle action.
     * @param {Object} evt.target - The target element of the event.
     * @param {boolean} evt.target.checked - Boolean indicating the current state of the toggle switch.
     * @throws {Error} Logs any errors encountered during the API call.
     */
    const handleScreenRecordingChange = async (evt) => {
        const sim = {...data},
            checked = !!evt.target.checked,
            propName = 'screen_recording';

        setScreenRecording(evt.target.checked);

        sim.levels[levelIndex].screenRecording = checked;

        try {
            const res = await toggleScreenRecordingSwitch({
                levelId: sim.levels[levelIndex]._id,
                screenRecording: checked,
            });
            setData(sim);
            emitOptionChanged(propName, checked, res.success);
        } catch (err) {
            console.error(err);
            emitOptionChanged(propName, checked, false);
        }
    };

    /**
     * Handles changes to the practice mode toggle. This function updates the practice mode state
     * both locally and on the server. It modifies the `play_mode` property of the current level
     * and triggers necessary side effects.
     *
     * @param {Object} evt - The event object from the practice mode toggle interaction.
     * @param {Object} evt.target - The target element of the event.
     * @param {boolean} evt.target.checked - The state of the practice mode toggle.
     *
     * @async
     * @throws Will log an error if the server update fails.
     */
    const handlePracticeModeChange = async (evt) => {
        const sim = {...data},
            checked = !!evt.target.checked,
            propName = 'practice_mode';

        setPracticeMode(checked);

        sim.levels[levelIndex].play_mode = checked ?
            sim.levels[levelIndex].play_mode | PLAYMODE_PRACTICE :
            sim.levels[levelIndex].play_mode & ~PLAYMODE_PRACTICE;

        try {
            const res = await togglePracticeModeSwitch({
                levelId: sim.levels[levelIndex]._id,
                practiceMode: checked,
            });
            setData(sim);
            emitOptionChanged(propName, checked, res.success);
        } catch (err) {
            console.error(err);
            emitOptionChanged(propName, checked, false);
        }
    };

    /**
     * Handles the change in autoplay mode for a specific level.
     * Updates the local state and sends an asynchronous request to toggle the autoplay mode.
     *
     * @async
     * @function handleAutoplayModeChange
     * @param {Object} evt - The event object triggered by the change in the autoplay mode.
     * The event contains the target element with the updated checked state.
     *
     * Modifies the playback mode of the specified level based on the checked state.
     * It updates the local data object and sends the changes to a server through the `toggleAutoplayModeSwitch` function.
     *
     * In case of success, the updated data is committed, and a corresponding event is emitted.
     * If an error occurs, it is logged and an event indicating the failure is emitted.
     */
    const handleAutoplayModeChange = async (evt) => {
        const sim = {...data},
            checked = !!evt.target.checked,
            propName = 'autoplay_mode';

        setAutoplayMode(evt.target.checked);

        sim.levels[levelIndex].play_mode = checked ?
            sim.levels[levelIndex].play_mode | PLAYMODE_AUTOPLAY :
            sim.levels[levelIndex].play_mode & ~PLAYMODE_AUTOPLAY;

        try {
            const res = await toggleAutoplayModeSwitch({
                levelId: sim.levels[levelIndex]._id,
                autoplayMode: checked,
            });
            setData(sim);
            emitOptionChanged(propName, checked, res.success);
        } catch (err) {
            console.error(err);
            emitOptionChanged(propName, checked, false);
        }
    };

    /**
     * Event handler function for handling the click event to generate audio.
     * This function checks if the data, level index, and scripts are properly
     *      defined and non-empty.
     * If the conditions are satisfied, it opens the audio component by setting
     *      the state to true and retrieves the audio count.
     *
     */
    const handleGenerateAudioClick = () => {
        if (data?.levels[levelIndex] && data?.levels[levelIndex]?.scripts?.length) {
            setAudioOpen(true);
            getAudioCount();
        }
    };

    /**
     * Generates a tooltip component that displays a list of settings for a specific level.
     *
     * The tooltip contains a series of entries indicating the status (enabled or disabled)
     * of each setting for the level. Each entry is styled with a specific font size, and the
     * status is color-coded (green for enabled, red for disabled).
     *
     * @function
     * @returns {JSX.Element} JSX markup representing the settings tooltip with styled status indicators for each entry.
     */
    const settingsTooltip = () => {
        return (
            <>{levelSettingsTooltip.map((tip, localIndex) => {
                    return (<p style={{fontSize: '1rem'}} key={localIndex}>
                        {tip.text} {(tip.status ? <span style={{color: 'green'}}>Enabled</span> :
                        <span style={{color: 'red'}}>Disabled</span>)} for this Level</p>)
                }
            )}</>
        );
    };

    return (
        <>
            {isPassFail && (
                <Tooltip style={{fontSize: '1rem'}}
                         title={"This level is marked as the Pass/Fail for the SIM"}
                >
                    <Button className={classes.closeButton}
                            style={{
                                padding: '0.25em',
                                width: 'fit-content',
                                height: '100%',
                                minWidth: 'unset'
                            }}
                    >
                        <AssignmentTurnedInIcon style={{
                            color: '#2CB174',
                            fontSize: '1rem',
                            height: '100%',
                            width: '1.25rem'
                        }}></AssignmentTurnedInIcon>
                    </Button>
                </Tooltip>
            )}
            {enabled ? (
                <Tooltip title={settingsTooltip()} style={{fontSize: '1rem'}}>
                    <Button className={classes.closeButton}
                            style={{
                                padding: '0.25em',
                                width: 'fit-content',
                                height: '100%',
                                minWidth: 'unset'
                            }}
                    >
                        <SettingsIcon onClick={handleSettingsClick}
                                      style={{
                                          fontSize: '1rem',
                                          width: '1.25rem',
                                          height: '100%'
                                      }}
                        />
                    </Button>
                </Tooltip>
            ) : (
                <Tooltip title="Please add at least one script to the level before adjusting any settings" style={{fontSize: '1rem'}}>
                    <Button className={classes.closeButton}
                            style={{
                                padding: '0.25em',
                                width: 'fit-content',
                                height: '100%',
                                minWidth: 'unset'
                            }}
                    >
                        <SettingsIcon style={{
                                fontSize: '1rem',
                                width: '1.25rem',
                                height: '100%'
                            }}
                            disabled={true}
                        />
                    </Button>
                </Tooltip>
            )}
            <Dialog onClose={handleClose} open={open} fullWidth maxWidth="md">
                <DialogTitle>
                    Settings for level {levelTitle}
                    <IconButton
                        aria-label="close"
                        className={classes.closeButton}
                        onClick={handleClose}
                        style={{float: "right"}}
                    >
                        <CloseIcon/>
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <div>
                        <Box className={classes.iconBox}>
                            <FormGroup>
                                <FormControlLabel
                                    label="Keyword Score - Display in Agent's Result Screen"
                                    control={<Switch checked={keywordScore}
                                                     onChange={handleKeywordChange}
                                    />}
                                />
                            </FormGroup>

                            <FormGroup>
                                <FormControlLabel label="Hints - Display during SIM for this Level"
                                                  control={<Switch checked={hintText}
                                                                   onChange={handleHintTextChange}
                                                  />}
                                />
                            </FormGroup>

                            {data.real_time_feedback ?
                                realTimeDisabled ? (
                                    <FormGroup>
                                        <Tooltip title="Real-time feedback is disabled because there are no Agent scripts in this level">
                                            <FormControlLabel label="Real-time Feedback - Display during SIM for this Level"
                                                              control={<Switch checked={realTimeFeedbackEnabled}
                                                                               onChange={handleRealTimeChange}
                                                                               disabled={realTimeDisabled}
                                                              />}
                                            />
                                        </Tooltip>
                                    </FormGroup>
                                ) : (
                                    <FormGroup>
                                        <FormControlLabel label="Real-time Feedback - Display during SIM for this Level"
                                                          control={<Switch checked={realTimeFeedbackEnabled}
                                                              onChange={handleRealTimeChange}
                                                              disabled={realTimeDisabled}
                                                          />}
                                        />
                                    </FormGroup>
                                )
                            : null}

                            <FormGroup>
                                <FormControlLabel label="Send to Snowfly - Display in Agent Result Screen"
                                                  control={<Switch checked={sendsToSnowfly}
                                                                   onChange={handleSendToSnowflyChange}
                                                  />}
                                />
                            </FormGroup>

                            <FormGroup>
                                <FormControlLabel label="Allow Video Recording - Allow Agent to record this Level"
                                                  control={<Switch checked={screenRecording}
                                                                   onChange={handleScreenRecordingChange}
                                                  />}
                                />
                            </FormGroup>

                            <FormGroup>
                                <FormControlLabel label="Enable Practice Mode - Display in Levels Start"
                                                  control={<Switch checked={practiceMode}
                                                                   onChange={handlePracticeModeChange}
                                                  />}
                                />
                            </FormGroup>

                            <FormGroup>
                                <FormControlLabel label="Enable Autoplay Mode - Display in Levels Start"
                                                  control={<Switch checked={autoplayMode}
                                                                   onChange={handleAutoplayModeChange}
                                                  />}
                                />
                            </FormGroup>
                        </Box>
                    </div>
                </DialogContent>
                <DialogActions>
                    <ButtonGroup style={{marginInline: '0 auto'}}>
                        <Button onClick={() => handlePassDialog('open')}
                        >
                            SET PASS/FAIL FOR THE SIM
                        </Button>
                        {audioCount ? (
                            <Button onClick={handleGenerateAudioClick}>
                                <VolumeUpRoundedIcon className={classes.iconVolume}
                                                     style={{marginRight: '0.25em'}}
                                />
                                Generate Audio
                            </Button>
                        ) : null}

                        {(levelIndex > 0 &&
                            <Button onClick={handleImportHint}>
                                Import Hint Text from Level 1
                                {copyingHintText ?
                                    (<LinearProgress style={{
                                        position: "absolute",
                                        left: "5%",
                                        top: "75%",
                                        width: "90%"
                                    }}/>) :
                                    null
                                }
                            </Button>
                        )}
                    </ButtonGroup>
                    <ButtonGroup style={{marginInline: 'auto 0'}}>
                        <Button onClick={() => setOpen(false)}>Close</Button>
                    </ButtonGroup>
                </DialogActions>
            </Dialog>
            <Dialog
                open={audioOpen}
                onClose={() => setAudioOpen(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    Generate Audio
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {`There are ${audioCount} audio clips already. Are you sure you want to record over them?`}
                    </DialogContentText>
                    <div className={classes.progress}>
                        {load ? <LinearProgress color="primary"/> : null}
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAudioOpen(false)} className={classes.cancelButton}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() => handleLevelAudioGenerate()}
                        className={classes.dialogDeleteBtn}
                        autoFocus
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            <PassFailCriteria
                handlePassDialog={handlePassDialog}
                passDialog={passDialog}
                data={data}
                currentIndex={levelIndex}
                setAlertType={setAlertType}
                setAlertMessage={setAlertMessage}
                setOpenAlert={setOpenAlert}
            />
        </>
    );
};

LevelOptions.propTypes = {
    data: PropTypes.object,
    setData: PropTypes.func,
    updateData: PropTypes.func,
    levelIndex: PropTypes.number,
    singleLevel: PropTypes.object,
    copyHintText: PropTypes.func,
    classes: PropTypes.object,
    passFailLevel: PropTypes.string,
    onOptionChange: PropTypes.func,
    setAlertType: PropTypes.func,
    setAlertMessage: PropTypes.func,
    setOpenAlert: PropTypes.func,
    setLoad: PropTypes.func,
    load: PropTypes.bool,
};

export default LevelOptions;
